import { FC, useEffect, useMemo } from "react";

import { Alert, FormCheck } from "react-bootstrap";
import {
  Controller,
  useController,
  UseControllerProps,
  useFieldArray,
  useFormContext,
} from "react-hook-form";

import { ServiceOrderType } from "@/_constants/serviceOrder";
import { BusinessServiceType } from "@/common/enums/sharedEnums";

import Loader from "../../../../common/components/Loader";
import { useGetServicesDiagnosticTypes } from "../../hooks/diagnosticType.hooks";
import { IDiagnosticDetailsServiceOrdering } from "../../interfaces";
import { DiagnosticTypeValue } from "../../reducers";
import { useServiceInventoryStore } from "../../store/ServiceInventoryStore";

const ServiceOrderingDiagnosticsForm: FC<UseControllerProps> = ({ name }) => {
  const orderServiceType = name?.split(".")[0] as ServiceOrderType;
  const { control } = useFormContext();
  const { businessServiceType } = useServiceInventoryStore((state) => state);

  const selectedDwelling = useServiceInventoryStore(
    (state) => state.selectedAddress
  );

  const { data, isLoading, error } = useGetServicesDiagnosticTypes(
    orderServiceType,
    selectedDwelling?.id
  );

  const { fields, replace } = useFieldArray({
    control,
    name,
  });

  const isPPPT_EDPC = (type: IDiagnosticDetailsServiceOrdering) => {
    return (
      businessServiceType === BusinessServiceType.CONDOMINIUM_MANAGEMENT &&
      orderServiceType === ServiceOrderType.PPPT &&
      type?.slug === DiagnosticTypeValue.EDPC
    );
  };

  const isCondominiumVisit = (type: IDiagnosticDetailsServiceOrdering) => {
    return (
      businessServiceType === BusinessServiceType.CONDOMINIUM_MANAGEMENT &&
      orderServiceType === ServiceOrderType.CONDOMINIUM_VISIT &&
      type?.slug === DiagnosticTypeValue.CONDOMINIUM_VISIT
    );
  };

  const businessDiagnosticTypes = useMemo(() => {
    return data?.diagnosticTypes.filter(
      (diagnosticType) => diagnosticType.slug === businessServiceType
    );
  }, [data?.diagnosticTypes, businessServiceType]);

  const { fieldState } = useController({ name, control });

  useEffect(() => {
    if (businessDiagnosticTypes && businessDiagnosticTypes.length > 0) {
      replace(
        businessDiagnosticTypes[0].diagnosticTypes.map(
          (type: IDiagnosticDetailsServiceOrdering) => {
            return {
              ...type,
              isChecked:
                isPPPT_EDPC(type) || isCondominiumVisit(type)
                  ? true
                  : Boolean(type.isChecked),
            };
          }
        )
      );
    } else {
      // Clear the form when no diagnostics are available (e.g., when address changes)
      replace([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDiagnosticTypes, replace, selectedDwelling?.id]);

  return (
    <div className="serviceOrdering_diagnostic">
      <div className="serviceOrdering-subtitle">Les diagnostics</div>
      <div className="serviceOrdering-text">
        Sélectionnez les diagnostics immobiliers à réaliser{" "}
      </div>
      {isLoading && <Loader />}
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="serviceOrdering_diagnostic-elements">
        {!isLoading &&
          !error &&
          fields?.map((diagnostic, index) => {
            return (
              <label
                key={diagnostic?.id}
                className={`serviceOrdering_diagnostic-element select-none hover:cursor-pointer ${
                  fieldState?.error &&
                  "serviceOrdering_diagnostic-element-error"
                }`}
              >
                <Controller
                  control={control}
                  name={`${name}.${index}.isChecked`}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <>
                        <div className="d-flex gap-3">
                          <FormCheck
                            type="checkbox"
                            onChange={(e) => onChange(e.currentTarget.checked)}
                            checked={value}
                          />
                          <span className="name">
                            {
                              (diagnostic as IDiagnosticDetailsServiceOrdering)
                                .name
                            }
                          </span>
                        </div>
                      </>
                    );
                  }}
                />
              </label>
            );
          })}
      </div>
      {fieldState?.error && (
        <span className="form-input_error-message">
          {fieldState.error?.message}
        </span>
      )}
      <div className="border-bottom" />
    </div>
  );
};

export default ServiceOrderingDiagnosticsForm;
